import type { CanvasSet } from "schemas/generated/canvas/document";
import type {
  AgentFile,
  AgentFileAccessibleSort,
  AgentFileContentResponse,
  AgentFileCreateRequest,
  AgentFileCreateResponse,
  AgentFileGrant,
  AgentFileGrantListResponse,
  AgentFileGrantUpsertRequest,
  AgentFileListResponse,
  AgentFileNamespace,
  AgentFilePageResponse,
  AgentFileReportContentRequest,
  AgentFileResponse,
  AgentFileRole,
  AgentFileRootView,
  AgentFileSandboxPathResponse,
  AgentFileShareListResponse,
  AgentFileUpdateRequest,
  AgentFileUploadRequest,
  AgentFileVisibility,
  AgentFileWriteUrlRequest,
  AgentFileWriteUrlResponse,
} from "schemas/generated/projectFile";
import type { createCanvasDocumentsClient } from "./canvasDocuments";
import type { RequestFn } from "./request";

import {
  CANVAS_SET_CONTENT_TYPE,
  getCanvasSetFileName,
  parseCanvasSetFileName,
} from "replo-utils/lib/canvasSetFiles";
import { randomUUID } from "replo-utils/lib/crypto";
import { emptyCanvasSet } from "schemas/canvas/document";
import { diffCanvasSets } from "schemas/canvas/operations";

export type MutationResult = { ok: true };

export type ListRootViewInput = {
  projectId: string;
  view: AgentFileRootView;
  namespace?: AgentFileNamespace;
};

export type ListAccessibleFilesInput = {
  projectId: string;
  search?: string;
  kind: "file" | "folder" | "all";
  includeAppFiles?: boolean;
  sort?: AgentFileAccessibleSort;
  offset?: number;
  limit?: number;
};

export type CreateFileInput = AgentFileCreateRequest & { projectId: string };
export type UploadFileInput = AgentFileUploadRequest & { projectId: string };
export type UpdateFileInput = AgentFileUpdateRequest & { fileId: string };
export type WriteUrlInput = AgentFileWriteUrlRequest & { fileId: string };
export type ReportContentInput = AgentFileReportContentRequest & {
  fileId: string;
};
export type UpsertGrantInput = Omit<AgentFileGrantUpsertRequest, "role"> & {
  fileId: string;
  role: AgentFileRole | null;
};

export type FilesClient = ReturnType<typeof createFilesClient>;

function encode(id: string): string {
  return encodeURIComponent(id);
}

// The row is created first because `onConflict: "rename"` may suffix the name,
// and the document's title must match the final filename or the agent's
// title-based lookup lands on the older set. The content then goes through the
// set's coordinator like any other edit, so no bytes leave the browser for
// storage directly.
export async function createCanvasSetFile({
  client,
  projectId,
  set,
  visibility,
}: {
  client: {
    files: FilesClient;
    canvasDocuments: ReturnType<typeof createCanvasDocumentsClient>;
  };
  projectId: string;
  set: CanvasSet;
  visibility?: AgentFileVisibility;
}): Promise<{ file: AgentFile; set: CanvasSet }> {
  const { file } = await client.files.create({
    projectId,
    parentId: null,
    name: getCanvasSetFileName({ title: set.name }),
    kind: "file",
    contentType: CANVAS_SET_CONTENT_TYPE,
    visibility,
    onConflict: "rename",
  });
  const titled: CanvasSet = {
    ...set,
    name: parseCanvasSetFileName(file.name)?.title ?? set.name,
  };
  const operations = diffCanvasSets({
    before: emptyCanvasSet(titled.name),
    after: titled,
  });
  if (operations.length > 0) {
    await client.canvasDocuments.applyCanvasOperations({
      setFileId: file.id,
      clientId: randomUUID(),
      baseRevision: 0,
      operations,
    });
  }
  return { file, set: titled };
}

/**
 * Agent Files client — the database-backed, UUID-identity Drive surface,
 * mirroring the `/api/v1/files/{id}` routes.
 */
export function createFilesClient({ request }: { request: RequestFn }) {
  return {
    copyAsset: (input: {
      projectId: string;
      assetId: string;
      parentId?: string | null;
      name?: string;
    }): Promise<AgentFile> => {
      return request<AgentFileResponse>({
        method: "POST",
        path: "/api/v1/files/copy-asset",
        body: input,
      }).then((response) => response.file);
    },
    listRootView: (
      input: ListRootViewInput,
    ): Promise<AgentFileListResponse> => {
      return request<AgentFileListResponse>({
        method: "GET",
        path: "/api/v1/files",
        query: {
          projectId: input.projectId,
          view: input.view,
          namespace: input.namespace,
        },
      });
    },
    // The most recently updated files across the whole project tree (flat, any
    // folder) — what `listRootView` can't give, since it only sees Drive roots.
    listRecent: (input: {
      projectId: string;
      namespace?: AgentFileNamespace;
      limit?: number;
    }): Promise<AgentFileListResponse> => {
      return request<AgentFileListResponse>({
        method: "GET",
        path: "/api/v1/files/recent",
        query: {
          projectId: input.projectId,
          namespace: input.namespace,
          limit: input.limit,
        },
      });
    },
    // Every file the caller can open, newest first unless `sort` says otherwise,
    // paged by `nextOffset`. App-owned files are skipped unless
    // `includeAppFiles`. No byte URLs: mint one with `getContent` for the file
    // actually used.
    listAccessible: (
      input: ListAccessibleFilesInput,
    ): Promise<AgentFilePageResponse> => {
      return request<AgentFilePageResponse>({
        method: "GET",
        path: "/api/v1/files/accessible",
        query: {
          projectId: input.projectId,
          search: input.search,
          kind: input.kind,
          includeAppFiles: input.includeAppFiles ? "true" : undefined,
          sort: input.sort,
          offset: input.offset,
          limit: input.limit,
        },
      });
    },
    listChildren: (input: {
      fileId: string;
    }): Promise<AgentFileListResponse> => {
      return request<AgentFileListResponse>({
        method: "GET",
        path: `/api/v1/files/${encode(input.fileId)}/children`,
      });
    },
    stat: (input: { fileId: string }): Promise<AgentFile> => {
      return request<AgentFileResponse>({
        method: "GET",
        path: `/api/v1/files/${encode(input.fileId)}`,
      }).then((response) => response.file);
    },
    getSandboxPath: (input: {
      fileId: string;
    }): Promise<AgentFileSandboxPathResponse> => {
      return request<AgentFileSandboxPathResponse>({
        method: "GET",
        path: `/api/v1/files/${encode(input.fileId)}/sandbox-path`,
      });
    },
    // Returns the created row plus, when content metadata was supplied, a
    // presigned `uploadUrl` for a direct R2 PUT (one round trip).
    create: (input: CreateFileInput): Promise<AgentFileCreateResponse> => {
      return request<AgentFileCreateResponse>({
        method: "POST",
        path: "/api/v1/files",
        body: input,
      });
    },
    // Uploads bytes through bedrock-api (same-origin) rather than a direct
    // browser PUT to R2, which the bucket's missing CORS headers would block.
    upload: (input: UploadFileInput): Promise<AgentFile> => {
      return request<AgentFileResponse>({
        method: "POST",
        path: "/api/v1/files/upload",
        body: input,
      }).then((response) => response.file);
    },
    // Presign a direct PUT; reportContent afterwards refreshes the row's cached size and type.
    getWriteUrl: (input: WriteUrlInput): Promise<AgentFileWriteUrlResponse> => {
      return request<AgentFileWriteUrlResponse>({
        method: "POST",
        path: `/api/v1/files/${encode(input.fileId)}/write-url`,
        body: {
          contentType: input.contentType,
          sizeBytes: input.sizeBytes,
          contentCondition: input.contentCondition,
          renameTo: input.renameTo,
        },
      });
    },
    reportContent: (input: ReportContentInput): Promise<AgentFile> => {
      return request<AgentFileResponse>({
        method: "POST",
        path: `/api/v1/files/${encode(input.fileId)}/content`,
        body: {
          contentType: input.contentType,
          sizeBytes: input.sizeBytes,
          guard: input.guard,
        },
      }).then((response) => response.file);
    },
    getContent: (input: {
      fileId: string;
      inspectContent?: boolean;
    }): Promise<AgentFileContentResponse> => {
      return request<AgentFileContentResponse>({
        method: "GET",
        path: `/api/v1/files/${encode(input.fileId)}/content`,
        query: input.inspectContent ? { inspectContent: "true" } : undefined,
      });
    },
    update: (input: UpdateFileInput): Promise<AgentFile> => {
      const { fileId, ...patch } = input;
      return request<AgentFileResponse>({
        method: "PATCH",
        path: `/api/v1/files/${encode(fileId)}`,
        body: patch,
      }).then((response) => response.file);
    },
    delete: (input: { fileId: string }): Promise<MutationResult> => {
      return request<MutationResult>({
        method: "DELETE",
        path: `/api/v1/files/${encode(input.fileId)}`,
      });
    },
    listShares: (input: {
      fileId: string;
    }): Promise<AgentFileShareListResponse> => {
      return request<AgentFileShareListResponse>({
        method: "GET",
        path: `/api/v1/files/${encode(input.fileId)}/shares`,
      });
    },
    // Project-audience grants plus the caller's own, so a listing can be labelled in one call.
    listProjectGrants: (input: {
      projectId: string;
    }): Promise<AgentFileGrant[]> => {
      return request<AgentFileGrantListResponse>({
        method: "GET",
        path: "/api/v1/files/grants",
        query: { projectId: input.projectId },
      }).then((response) => response.grants);
    },
    upsertGrant: (input: UpsertGrantInput): Promise<AgentFileGrant | null> => {
      return request<{ grant: AgentFileGrant | null }>({
        method: "PUT",
        path: `/api/v1/files/${encode(input.fileId)}/shares`,
        body: {
          audience: input.audience,
          userId: input.userId,
          role: input.role,
        },
      }).then((response) => response.grant);
    },
    revokeGrant: (input: {
      fileId: string;
      userId: string;
    }): Promise<MutationResult> => {
      return request<MutationResult>({
        method: "DELETE",
        path: `/api/v1/files/${encode(input.fileId)}/shares/${encode(input.userId)}`,
      });
    },
    // Un-share a file from the whole team (revoke its project-audience grant).
    revokeProjectShare: (input: {
      fileId: string;
    }): Promise<MutationResult> => {
      return request<MutationResult>({
        method: "DELETE",
        path: `/api/v1/files/${encode(input.fileId)}/shares/team`,
      });
    },
  };
}
